/**
 * Rich presence, "Playing X" (2026-10-29), as Discord does it: the desktop
 * app's shell detects the game (bridge `games`); this module reports it in
 * op 3 PRESENCE_UPDATE, only when it changes, and only while "Share my
 * activity" (`show_current_game`) and the console's `rich_presence` flag
 * are on. The web app detects nothing but shows others' games from their
 * presences (`playingOf`).
 */
import {
  playingActivity,
  playingOf,
  type Activity,
  type DesktopDetectedGame,
  type DesktopGameState,
  type GatewayDispatch,
  type Snowflake,
} from '@neosocial/protocol';
import { create } from 'zustand';
import { desktopBridge } from '../lib/config';
import { flagOn, useConfig } from '../store/config';
import { useData } from '../store/data';
import type { DataState } from '../store/reducer';
import { presenceOf } from '../store/selectors';
import { onGatewayEvent } from '../store/umEvents';

interface GamesState {
  /** The game the shell reports now (null: none, or the web). */
  current: DesktopDetectedGame | null;
  /** Registered games, as the shell last said (null until asked, and on the web). */
  state: DesktopGameState | null;
}

export const useGames = create<GamesState>(() => ({ current: null, state: null }));

/** The activities this device reports: the game, when it may be shared. */
export function myActivities(): Activity[] {
  const g = useGames.getState().current;
  if (!g || !flagOn('rich_presence')) return [];
  if (useData.getState().settings?.show_current_game === false) return [];
  return [playingActivity(g.name, g.startedAt)];
}

/** What the server was last told (op 3, or IDENTIFY), so a repeat sends nothing. */
let lastSent = '[]';

/**
 * Sends op 3 with these activities on the live gateway; false when there is
 * none. session.ts sets it (this module never imports the session, so
 * importing it from the desktop glue pulls in nothing else).
 */
type ActivitySender = (activities: Activity[]) => boolean;
let sender: ActivitySender | null = null;

export function setActivitySender(fn: ActivitySender | null): void {
  sender = fn;
}

/** Sends op 3 when the reported activity changed (small payloads). */
export function pushActivity(): void {
  const activities = myActivities();
  const key = JSON.stringify(activities);
  if (key === lastSent) return;
  if (!sender || useData.getState().connection !== 'ready') return;
  if (sender(activities)) lastSent = key;
}

/**
 * The activities for an op 3 or IDENTIFY that session.ts sends (a status
 * change, a new connection), recorded as sent: every presence this device
 * sends carries the game, or the server would drop it.
 */
export function reportedActivities(): Activity[] {
  const activities = myActivities();
  lastSent = JSON.stringify(activities);
  return activities;
}

/** READY brought the account's settings: share or not, as they say (a resume keeps the server's). */
function onFrame(frame: GatewayDispatch): void {
  if (frame.t === 'READY') pushActivity();
}

let connected = false;
let stopFrames: (() => void) | null = null;

/** Starts game detection in the desktop app (once). Does nothing on the web. */
export function connectGameDetection(): void {
  const games = desktopBridge()?.games;
  if (!games || connected) return;
  connected = true;
  // registered here, not when the module loads: umEvents may still be loading then
  stopFrames = onGatewayEvent(onFrame);
  games.onChange((current) => {
    useGames.setState({ current });
    pushActivity();
    // Registered games' "Running" follows
    void refreshGames();
  });
  void refreshGames();
  // "Share my activity" or the console's flag changed: report (or stop reporting) at once
  useData.subscribe((s, prev) => {
    if (s.settings?.show_current_game !== prev.settings?.show_current_game) pushActivity();
    if (s.connection === 'ready' && prev.connection !== 'ready') pushActivity();
  });
  useConfig.subscribe((s, prev) => {
    if (s.config?.flags.rich_presence !== prev.config?.flags.rich_presence) pushActivity();
  });
}

/** Asks the shell for Registered games again. */
export async function refreshGames(): Promise<DesktopGameState | null> {
  const games = desktopBridge()?.games;
  if (!games) return null;
  try {
    const state = await games.getState();
    useGames.setState({ state, current: state.current });
    pushActivity();
    return state;
  } catch {
    return null;
  }
}

/** The game someone is playing as others see it, or null (offline: nothing). */
export function playingOfUser(
  s: DataState,
  userId: Snowflake,
): { name: string; start: number | null } | null {
  if (presenceOf(s, userId) === 'offline') return null;
  const a = playingOf(s.presences[userId]);
  if (!a) return null;
  const start = a.timestamps?.start ?? a.created_at;
  return { name: a.name, start: typeof start === 'number' ? start : null };
}

/** For tests: forget what was sent and detected. */
export function resetActivityForTests(): void {
  lastSent = '[]';
  connected = false;
  stopFrames?.();
  stopFrames = null;
  useGames.setState({ current: null, state: null });
}
